import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Modal, Box } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faHeadset,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import './LiveAgentInfoModal.css';

/**
 * Shown when the bot owner clicks the "Talk to a human" button inside the
 * in-portal test chat. The button is rendered there for parity with what
 * real visitors see, but starting an actual live-agent session from the
 * test modal would be paradoxical (the owner is the agent — accepting the
 * request in the notification would also navigate them away from the test
 * chat). Instead we explain the situation and offer two clear next steps:
 *
 *   1. Open the Live Sessions tab — to monitor / handle real visitor
 *      requests.
 *   2. Open the public chat link in a new tab — so the owner can perform a
 *      proper end-to-end test as a visitor while staying logged in as the
 *      agent here.
 */
const LiveAgentInfoModal = ({ open, onClose, onOpenLiveSessions, publicChatUrl }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '12px',
          boxShadow: 24,
          p: isMobile ? '20px' : '30px',
          width: isMobile ? '88%' : '90%',
          maxWidth: 480,
          outline: 'none',
        }}
      >
        <button className="lai-modal-close" onClick={onClose} aria-label={t('close')}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className="lai-modal-icon" aria-hidden="true">
          <FontAwesomeIcon icon={faHeadset} />
        </div>

        <h2 className="lai-modal-title">{t('live_agent_info_title')}</h2>

        <div className="lai-modal-body">
          <p>
            <Trans
              i18nKey="live_agent_info_body1_html"
              components={{ strong: <strong /> }}
            />
          </p>
          <p style={{ marginBottom: 0 }}>{t('live_agent_info_body2')}</p>
        </div>

        <div className="lai-modal-actions">
          {publicChatUrl && (
            <a
              className="lai-modal-btn"
              href={publicChatUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
              <span>{t('live_agent_info_open_public_chat')}</span>
            </a>
          )}
          <button
            className="lai-modal-btn ghost"
            onClick={onClose}
            type="button"
          >
            {t('live_agent_info_stay_here')}
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default LiveAgentInfoModal;
